import { useEffect, useState } from "react";
import { X, ZoomIn, ZoomOut, Maximize2 } from "lucide-react";

export function MenuThumb({ page, onOpen, className = "" }) {
    return (
        <button
            type="button"
            onClick={() => onOpen(page)}
            data-testid={`${page.testId}-open-btn`}
            aria-label={`Open ${page.label} full screen`}
            className={`group relative block w-full mx-auto cursor-zoom-in ${className}`}
        >
            <img
                src={page.src}
                alt={page.alt}
                width={page.w}
                height={page.h}
                loading="eager"
                data-testid={page.testId}
                className="block w-full h-auto bg-black shadow-[0_24px_80px_-20px_rgba(0,0,0,0.25)] transition-transform duration-500 group-hover:scale-[1.01]"
            />
            <span className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-white/90 text-[#121212] text-[11px] font-semibold uppercase tracking-[0.14em] px-3 py-2 rounded-full shadow-md">
                <Maximize2 className="w-3.5 h-3.5" /> Tap to zoom
            </span>
        </button>
    );
}

export function MenuLightbox({ page, onClose }) {
    const [zoomed, setZoomed] = useState(false);

    useEffect(() => {
        if (!page) return;
        setZoomed(false);
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        window.__lenis?.stop();
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            window.__lenis?.start();
            document.body.style.overflow = "";
        };
    }, [page, onClose]);

    if (!page) return null;
    const landscape = page.w > page.h;
    const baseW = landscape ? "w-full max-w-[1500px]" : "w-full max-w-[760px]";

    return (
        <div
            data-testid="menu-lightbox"
            data-lenis-prevent
            role="dialog"
            aria-modal="true"
            aria-label={page.label}
            className="fixed inset-0 z-[200] bg-black/95 overflow-auto overscroll-contain"
            onClick={onClose}
        >
            <div className="fixed top-4 right-4 z-[201] flex gap-2">
                <button
                    type="button"
                    data-testid="menu-lightbox-zoom-btn"
                    aria-label={zoomed ? "Zoom out" : "Zoom in"}
                    onClick={(e) => {
                        e.stopPropagation();
                        setZoomed((z) => !z);
                    }}
                    className="w-11 h-11 rounded-full bg-white text-[#121212] flex items-center justify-center shadow-lg transition-transform active:scale-95"
                >
                    {zoomed ? <ZoomOut className="w-5 h-5" /> : <ZoomIn className="w-5 h-5" />}
                </button>
                <button
                    type="button"
                    data-testid="menu-lightbox-close-btn"
                    aria-label="Close"
                    onClick={onClose}
                    className="w-11 h-11 rounded-full bg-[#EB4A18] text-white flex items-center justify-center shadow-lg transition-transform active:scale-95"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>
            <div className={zoomed ? "w-max p-4 pt-20 md:p-10" : "min-h-full flex items-start md:items-center justify-center p-4 pt-20 md:p-10"}>
                <img
                    src={page.src}
                    alt={page.alt}
                    data-testid="menu-lightbox-image"
                    onClick={(e) => {
                        e.stopPropagation();
                        setZoomed((z) => !z);
                    }}
                    className={`block h-auto ${zoomed ? "w-[220vw] max-w-none md:w-[160vw] cursor-zoom-out" : `${baseW} cursor-zoom-in`}`}
                />
            </div>
        </div>
    );
}
